// CUSTOM: personalised-library-v2.js
// v2 library page controller — user state detection, product pool parsing,
// selling plan normalisation, and event orchestration.
//
// Loaded with defer on page.personalised-library-v2.liquid.
// Executes after all other deferred modules (storage-manager, config, bundle-state, etc.).
// Exposes: window.personalised.libraryV2 — { state, productMap }
// Fires:   plv2:ready — once after init, with state payload

(function () {
  'use strict';

  window.personalised = window.personalised || {};

  // ---------------------------------------------------------------------------
  // 1. User state detection
  // ---------------------------------------------------------------------------

  /**
   * Read user state from localStorage.
   * All reads are wrapped in try/catch — malformed JSON or missing keys are
   * treated as absent data rather than errors.
   *
   * @returns {{ hasReco: boolean, bundleItems: Array, recoProductIds: Array }}
   */
  function detectUserState() {
    var hasReco = false;
    var bundleItems = [];
    var recoProductIds = [];

    // Read from the shared user state set by personalised-user-state.js.
    // hasReco is true only when vitable_recommended_products exists in localStorage.
    try {
      hasReco = !!(window.personalised && window.personalised.userState && window.personalised.userState.hasReco);
    } catch (e) {
      console.warn('[LibraryV2] Could not read userState:', e);
    }

    try {
      var bundleRaw = localStorage.getItem('personalised_bundle_state');
      if (bundleRaw) {
        var bundleState = JSON.parse(bundleRaw);
        if (bundleState && Array.isArray(bundleState.items)) {
          bundleItems = bundleState.items;
        }
      }
    } catch (e) {
      console.warn('[LibraryV2] Could not parse bundle state — treating as no-pack user:', e);
    }

    try {
      var recoRaw = localStorage.getItem('vitable.recommendation');
      if (recoRaw) {
        var reco = JSON.parse(recoRaw);
        if (reco && Array.isArray(reco.productIds)) {
          recoProductIds = reco.productIds;
        }
      }
    } catch (e) {
      console.warn('[LibraryV2] Could not parse recommendation data — treating as empty:', e);
    }

    // Quiz health goals — used by Mode 2 to sort and highlight goal pills.
    // Checks the server-injected customer metafield first (populated by a future
    // backend PR), then falls back to the recommendation localStorage entry.
    // Gracefully defaults to [] when neither source is available (AC-10 fallback).
    var quizGoals = [];
    try {
      var mf = window.__vitableMetafields;
      if (mf && Array.isArray(mf.health_goals) && mf.health_goals.length > 0) {
        quizGoals = mf.health_goals.map(function (g) { return String(g).toLowerCase(); });
      }
    } catch (e) { /* treat as absent */ }

    if (quizGoals.length === 0) {
      try {
        // recommendation.js writes vitable_goals as a normalised lowercase array
        // e.g. ['energy', 'sleep', 'hair'] after normalizeGoals() runs on the API response.
        var vitableGoalsRaw = localStorage.getItem('vitable_goals');
        if (vitableGoalsRaw) {
          var vitableGoals = JSON.parse(vitableGoalsRaw);
          if (Array.isArray(vitableGoals) && vitableGoals.length > 0) {
            quizGoals = vitableGoals.map(function (g) { return String(g).toLowerCase(); });
          }
        }
      } catch (e) {
        console.warn('[LibraryV2] Could not read quiz goals from localStorage:', e);
      }
    }

    return {
      hasReco: hasReco,
      bundleItems: bundleItems,
      recoProductIds: recoProductIds,
      quizGoals: quizGoals
    };
  }

  // ---------------------------------------------------------------------------
  // 3. Selling plan parsing
  // ---------------------------------------------------------------------------

  /**
   * Parse the #v2-selling-plans JSON block and populate window.personalised.sellingPlans
   * in v1-compatible format: { [variantId]: { [lc_underscored_plan_name]: planId } }.
   *
   * Keys are stored under both the string variantId and its integer equivalent so that
   * bundle-state.js normaliseShopifyId lookups work regardless of whether they return
   * a string or number.
   */
  function parseSellingPlans() {
    var el = document.getElementById('v2-selling-plans');
    if (!el) return;

    try {
      var raw = JSON.parse(el.textContent);
      window.personalised.sellingPlans = window.personalised.sellingPlans || {};

      Object.keys(raw).forEach(function (variantIdStr) {
        var plans = raw[variantIdStr];
        var normalised = {};

        Object.keys(plans).forEach(function (planName) {
          // Normalise to lowercase with underscores — matches v1 selling plan label format
          var key = planName.toLowerCase().replace(/[\s-]+/g, '_');
          normalised[key] = plans[planName];
        });

        // Store under string key
        window.personalised.sellingPlans[variantIdStr] = normalised;
        // Also store under integer key for bundle-state.js compatibility
        var intKey = parseInt(variantIdStr, 10);
        if (!isNaN(intKey)) {
          window.personalised.sellingPlans[intKey] = normalised;
        }
      });
    } catch (e) {
      console.warn('[LibraryV2] Could not parse selling plans JSON:', e);
    }
  }

  // ---------------------------------------------------------------------------
  // 4. Product pool parsing
  // ---------------------------------------------------------------------------

  /**
   * Build a Map<productId, ProductData> from the hidden .plv2-product-pool div.
   * Each entry contains all data attributes plus parsed goal categories and
   * goal descriptions from their accompanying JSON script blocks.
   *
   * This map is the single source of truth for all modes in subsequent PRs.
   *
   * @returns {Map<string, Object>}
   */
  function parseProductPool() {
    var productMap = new Map();
    var pool = document.querySelector('.plv2-product-pool');
    if (!pool) return productMap;

    var cards = pool.querySelectorAll('.plv2-product-data[data-product-id]');

    cards.forEach(function (card) {
      var productId = card.dataset.productId;
      if (!productId) return;

      var goals = [];
      var goalsEl = document.getElementById('v2-product-goals-' + productId);
      if (goalsEl) {
        try {
          goals = JSON.parse(goalsEl.textContent) || [];
        } catch (e) {
          console.warn('[LibraryV2] Could not parse goals for product ' + productId);
        }
      }

      var goalDescriptions = {};
      var descEl = document.getElementById('v2-goal-descriptions-' + productId);
      if (descEl) {
        try {
          var parsed = JSON.parse(descEl.textContent);
          // Guard against double-encoding: if the metafield is a text type (not json type)
          // the | json filter double-encodes it, so JSON.parse returns a string not an object.
          if (typeof parsed === 'string') {
            parsed = JSON.parse(parsed);
          }
          goalDescriptions = parsed || {};
        } catch (e) {
          console.warn('[LibraryV2] Could not parse goal descriptions for product ' + productId, e);
        }
      }

      productMap.set(productId, {
        productId: productId,
        variantId: card.dataset.variantId || '',
        handle: card.dataset.productHandle || '',
        title: card.dataset.productTitle || '',
        available: card.dataset.productAvailable === 'true',
        sortOrder: parseInt(card.dataset.sortOrder, 10) || 9999,
        bannerImage: card.dataset.bannerImage || '',
        shortDescription: card.dataset.shortDescription || '',
        price: parseInt(card.dataset.price, 10) || 0,
        tags: card.dataset.tags
          ? card.dataset.tags.split(',').map(function (t) { return t.trim(); }).filter(Boolean)
          : [],
        goals: goals,
        goalDescriptions: goalDescriptions
      });
    });

    return productMap;
  }

  // ---------------------------------------------------------------------------
  // 5. Loading skeleton
  // ---------------------------------------------------------------------------

  function _hideSkeleton() {
    var skeleton = document.getElementById('plv2-skeleton');
    if (skeleton) { skeleton.setAttribute('hidden', ''); }
    var page = document.getElementById('plv2-page');
    if (page) { page.removeAttribute('hidden'); }
  }

  // ---------------------------------------------------------------------------
  // 6. Initialisation
  // ---------------------------------------------------------------------------

  document.addEventListener('DOMContentLoaded', function () {
    try {

    var state = detectUserState();

    // Hydrate the in-memory bundle from localStorage so addItem / removeItem
    // operate on the existing bundle rather than an empty array.
    var bundle = window.personalised && window.personalised.bundle;
    if (bundle && typeof bundle.load === 'function') {
      bundle.load();
    }

    // Parse product pool from DOM into a JS Map
    var productMap = parseProductPool();

    // Empty collection guard
    if (productMap.size === 0) {
      _hideSkeleton();
      var emptyEl = document.createElement('p');
      emptyEl.className = 'plv2-empty-state';
      emptyEl.textContent = 'No products available right now. Check back soon.';
      var page = document.getElementById('plv2-page');
      if (page) { page.appendChild(emptyEl); }
      return;
    }

    // Populate window.personalised.sellingPlans from the JSON block
    parseSellingPlans();

    // Expose the v2 namespace — filter controller reads this on plv2:ready
    window.personalised.libraryV2 = {
      state: state,
      productMap: productMap
    };

    // Dispatch plv2:ready — filter controller and other listeners init here
    document.dispatchEvent(new CustomEvent('plv2:ready', {
      bubbles: true,
      detail: {
        hasReco: state.hasReco,
        bundleItemCount: state.bundleItems.length,
        recoProductIds: state.recoProductIds,
        productCount: productMap.size
      }
    }));

    // Hide loading skeleton
    _hideSkeleton();

    // ---------------------------------------------------------------------------
    // Hash-based deep linking (simplified — goals and filters only)
    // ---------------------------------------------------------------------------
    (function _applyHashRouting() {
      var hash = location.hash.slice(1).toLowerCase();
      if (!hash) { return; }

      var fc = window.personalised.filterController;
      if (!fc) { return; }

      // Known filter aliases
      var FILTERS = {
        'for-you':      'recommended',
        'in-pack':      'in-pack',
        'vitamins':     'vitamins',
        'minerals':     'minerals',
        'herbs':        'herbs',
        'probiotics':   'probiotics',
        'specialty':    'specialty',
        'powders':      'powders'
      };

      if (FILTERS[hash]) {
        fc.setFilter(FILTERS[hash]);
      } else {
        // Treat as a goal handle
        fc.setGoal(hash);
      }

      console.debug('[LibraryV2] Hash routing applied — hash:', hash);
    })();

    console.debug('[LibraryV2] Initialised — products:', productMap.size);

    } catch (err) {
      console.error('[LibraryV2] Initialisation failed:', err);
      _hideSkeleton();
    }
  });

}());
